import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Crown, Check, X, Gift, Pause, MessageCircle, AlertTriangle, ArrowRight, AlertCircle } from 'lucide-react';

const SubscriptionPage = ({ onAuthClick }) => {
  const { user, subscription, getSubscriptionStatus, isAuthenticated } = useAuth();
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelReason, setCancelReason] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const status = getSubscriptionStatus();

  const cancelReasons = [
    { id: 'expensive', label: 'C\'est trop cher', emoji: '💰' },
    { id: 'not-using', label: 'Je ne l\'utilise pas assez', emoji: '📉' },
    { id: 'not-helpful', label: 'Ça ne m\'aide pas', emoji: '😕' },
    { id: 'found-alternative', label: 'J\'ai trouvé une alternative', emoji: '🔄' },
    { id: 'temporary', label: 'Pause temporaire', emoji: '⏸️' },
    { id: 'other', label: 'Autre raison', emoji: '💬' }
  ];

  const handleStartCancel = () => {
    setShowCancelFlow(true);
    setCancelStep(1);
  };

  const handleSelectReason = (reason) => {
    setCancelReason(reason);
    setCancelStep(2);
  };

  const handleAcceptOffer = (offer) => {
    setShowSuccess(true);
    setShowCancelFlow(false);
    setCancelStep(1);
  };

  const handleFinalCancel = () => {
    // Ouvrir le Customer Portal Stripe
    window.open('https://billing.stripe.com/p/login/4gMaEX62y94r2o6cUMdwc00', '_blank');
    setShowCancelFlow(false);
  };

  const handleManageSubscription = () => {
    window.open('https://billing.stripe.com/p/login/4gMaEX62y94r2o6cUMdwc00', '_blank');
  };

  if (!isAuthenticated) {
    return (
      <div className="subscription-page">
        <div className="profile-locked">
          <div className="locked-icon">🔒</div>
          <h2>Gérez votre abonnement</h2>
          <p>Connectez-vous pour voir et gérer votre abonnement MonCoach.</p>
          <button className="cta-button" onClick={onAuthClick}>
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="page-header">
        <div className="header-content">
          <h1>👑 Mon Abonnement</h1>
          <p>Gérez votre abonnement MonCoach Pro</p>
        </div>
      </div>

      <div className="subscription-content">
        {/* Current Plan Card */}
        <div className={`plan-card current ${status === 'cancelled' ? 'cancelled' : ''}`}>
          <div className={`plan-header ${status === 'cancelled' ? 'cancelled' : ''}`}>
            <div className="plan-icon">
              {status === 'pro' && <Crown size={32} />}
              {status === 'trial' && <Gift size={32} />}
              {status === 'free' && <MessageCircle size={32} />}
              {status === 'cancelled' && <AlertCircle size={32} />}
            </div>
            <div className="plan-info">
              <h2>
                {status === 'pro' && 'MonCoach Pro'}
                {status === 'trial' && 'Essai Pro'}
                {status === 'free' && 'MonCoach Gratuit'}
                {status === 'cancelled' && 'Abonnement annulé'}
              </h2>
              <span className={`status-badge ${status}`}>
                {status === 'pro' && '✓ Actif'}
                {status === 'trial' && 'Essai en cours'}
                {status === 'free' && 'Gratuit'}
                {status === 'cancelled' && 'Annulé'}
              </span>
            </div>
          </div>

          <div className="plan-details">
            <div className="detail-row">
              <span>Email</span>
              <strong>{user?.email}</strong>
            </div>

            {status === 'pro' && (
              <>
                <div className="detail-row">
                  <span>Plan</span>
                  <strong>{subscription?.plan_type === 'monthly' ? 'Pro Mensuel' : 'Pro Annuel'}</strong>
                </div>
                <div className="detail-row">
                  <span>Prix</span>
                  <strong>{subscription?.plan_type === 'monthly' ? '8,99€/mois' : '54,99€/an'}</strong>
                </div>
                {subscription?.current_period_end && (
                  <div className="detail-row">
                    <span>Prochain paiement</span>
                    <strong>{new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}</strong>
                  </div>
                )}
              </>
            )}

            {status === 'trial' && (
              <>
                <div className="detail-row">
                  <span>Fin de l'essai</span>
                  <strong>{subscription?.trial_end 
                    ? new Date(subscription.trial_end).toLocaleDateString('fr-FR')
                    : '---'}</strong>
                </div>
                <div className="trial-warning">
                  <AlertTriangle size={18} />
                  <span>Ton essai se termine bientôt ! Passe Pro pour continuer.</span>
                </div>
              </>
            )}

            {status === 'cancelled' && (
              <>
                {subscription?.cancel_at && (
                  <div className="detail-row">
                    <span>Accès jusqu'au</span>
                    <strong>{new Date(subscription.cancel_at).toLocaleDateString('fr-FR')}</strong>
                  </div>
                )}
                <div className="cancelled-warning">
                  <AlertCircle size={18} />
                  <span>Votre abonnement a été annulé. Vous perdrez l'accès Pro à la fin de votre période.</span>
                </div>
              </>
            )}

            {status === 'free' && (
              <div className="detail-row">
                <span>Messages</span>
                <strong>Limité à 10/mois</strong>
              </div>
            )}
          </div>

          <div className="plan-features">
            <h4>
              {status === 'cancelled' ? 'Vous allez perdre :' : 'Inclus dans votre plan :'}
            </h4>
            {(status === 'free' || status === 'cancelled') ? (
              <ul>
                <li><X size={16} className="disabled" /> Messages illimités</li>
                <li><X size={16} className="disabled" /> Historique complet</li>
                <li><X size={16} className="disabled" /> Recettes personnalisées</li>
                <li><X size={16} className="disabled" /> Support prioritaire</li>
              </ul>
            ) : (
              <ul>
                <li><Check size={16} /> Messages illimités</li>
                <li><Check size={16} /> Historique complet</li>
                <li><Check size={16} /> Recettes personnalisées</li>
                <li><Check size={16} /> Support prioritaire</li>
              </ul>
            )}
          </div>

          {/* Actions selon le statut */}
          {status === 'free' && (
            <div className="upgrade-options">
              <h4>Passe Pro et débloques tout :</h4>
              <div className="pricing-options">
                <div className="pricing-option" onClick={() => window.open('https://buy.stripe.com/4gMaEX62y94r2o6cUMdwc00', '_blank')}>
                  <div className="pricing-name">Mensuel</div>
                  <div className="pricing-price">8,99€<span>/mois</span></div>
                  <div className="pricing-desc">Sans engagement</div>
                  <button className="pricing-btn">Choisir</button>
                </div>
                <div className="pricing-option popular" onClick={() => window.open('https://buy.stripe.com/28EaEX9eK3K7geW9IAdwc01', '_blank')}>
                  <div className="pricing-badge">-49%</div>
                  <div className="pricing-name">Annuel</div>
                  <div className="pricing-price">54,99€<span>/an</span></div>
                  <div className="pricing-desc">Soit 4,58€/mois</div>
                  <button className="pricing-btn">Choisir</button>
                </div>
              </div>
              <p className="trial-note">🎁 7 jours d'essai gratuit inclus !</p>
            </div>
          )}

          {status === 'cancelled' && (
            <div className="resubscribe-section">
              <h4>🎁 Revenez parmi nous !</h4>
              <p>Réactivez votre abonnement et récupérez tous vos avantages Pro.</p>
              <div className="pricing-options">
                <div className="pricing-option" onClick={() => window.open('https://buy.stripe.com/4gMaEX62y94r2o6cUMdwc00', '_blank')}>
                  <div className="pricing-name">Mensuel</div>
                  <div className="pricing-price">8,99€<span>/mois</span></div>
                  <button className="pricing-btn">Réactiver</button>
                </div>
                <div className="pricing-option popular" onClick={() => window.open('https://buy.stripe.com/28EaEX9eK3K7geW9IAdwc01', '_blank')}>
                  <div className="pricing-badge">-49%</div>
                  <div className="pricing-name">Annuel</div>
                  <div className="pricing-price">54,99€<span>/an</span></div>
                  <button className="pricing-btn">Réactiver</button>
                </div>
              </div>
            </div>
          )}

          {status === 'pro' && (
            <div className="pro-actions">
              <button className="manage-btn" onClick={handleManageSubscription}>
                Gérer mon abonnement
              </button>
              <button className="cancel-btn" onClick={handleStartCancel}>
                Annuler mon abonnement
              </button>
            </div>
          )}

          {status === 'trial' && (
            <div className="upgrade-options">
              <h4>Passe Pro avant la fin de ton essai :</h4>
              <div className="pricing-options">
                <div className="pricing-option" onClick={() => window.open('https://buy.stripe.com/4gMaEX62y94r2o6cUMdwc00', '_blank')}>
                  <div className="pricing-name">Mensuel</div>
                  <div className="pricing-price">8,99€<span>/mois</span></div>
                  <button className="pricing-btn">Choisir</button>
                </div>
                <div className="pricing-option popular" onClick={() => window.open('https://buy.stripe.com/28EaEX9eK3K7geW9IAdwc01', '_blank')}>
                  <div className="pricing-badge">-49%</div>
                  <div className="pricing-name">Annuel</div>
                  <div className="pricing-price">54,99€<span>/an</span></div>
                  <button className="pricing-btn">Choisir</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Benefits reminder for Pro users */}
        {status === 'pro' && (
          <div className="benefits-reminder">
            <h3>🎉 Merci d'être Pro !</h3>
            <p>Grâce à vous, MonCoach peut continuer à vous accompagner dans votre transformation.</p>
            <div className="stats-row">
              <div className="stat">
                <strong>∞</strong>
                <span>Messages</span>
              </div>
              <div className="stat">
                <strong>24/7</strong>
                <span>Disponibilité</span>
              </div>
              <div className="stat">
                <strong>100%</strong>
                <span>Personnalisé</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Flow Modal */}
      {showCancelFlow && (
        <div className="modal-overlay" onClick={() => setShowCancelFlow(false)}>
          <div className="cancel-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCancelFlow(false)}>
              <X size={20} />
            </button>

            {cancelStep === 1 && (
              <div className="cancel-step">
                <div className="cancel-header">
                  <span className="cancel-emoji">😢</span>
                  <h2>On est triste de te voir partir...</h2>
                  <p>Dis-nous pourquoi tu veux annuler, on peut peut-être t'aider !</p>
                </div>

                <div className="reasons-grid">
                  {cancelReasons.map(reason => (
                    <button
                      key={reason.id}
                      className="reason-btn"
                      onClick={() => handleSelectReason(reason.id)}
                    >
                      <span>{reason.emoji}</span>
                      <span>{reason.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {cancelStep === 2 && (
              <div className="cancel-step">
                {cancelReason === 'expensive' && (
                  <>
                    <div className="cancel-header">
                      <span className="cancel-emoji">🎁</span>
                      <h2>On a une offre pour toi !</h2>
                      <p>Reste avec nous à prix réduit</p>
                    </div>

                    <div className="offer-card special">
                      <div className="offer-badge">-50% À VIE</div>
                      <h3>4,49€/mois</h3>
                      <p>au lieu de 8,99€/mois</p>
                      <ul>
                        <li><Check size={16} /> Messages illimités</li>
                        <li><Check size={16} /> Toutes les fonctionnalités Pro</li>
                        <li><Check size={16} /> Prix bloqué à vie</li>
                      </ul>
                      <button className="accept-offer-btn" onClick={() => handleAcceptOffer('50off')}>
                        J'en profite ! 🎉
                      </button>
                    </div>

                    <button className="skip-offer" onClick={() => setCancelStep(3)}>
                      Non merci, je veux quand même annuler
                    </button>
                  </>
                )}

                {cancelReason === 'not-using' && (
                  <>
                    <div className="cancel-header">
                      <span className="cancel-emoji">⏸️</span>
                      <h2>Et si tu faisais une pause ?</h2>
                      <p>On peut mettre ton abonnement en pause gratuitement</p>
                    </div>

                    <div className="offer-card">
                      <Pause size={40} />
                      <h3>Pause de 1 mois</h3>
                      <p>Ton abonnement sera suspendu pendant 1 mois, puis reprendra automatiquement.</p>
                      <button className="accept-offer-btn" onClick={() => handleAcceptOffer('pause')}>
                        <Pause size={18} />
                        Mettre en pause
                      </button>
                    </div>

                    <button className="skip-offer" onClick={() => setCancelStep(3)}>
                      Non, je préfère annuler
                    </button>
                  </>
                )}

                {cancelReason === 'not-helpful' && (
                  <>
                    <div className="cancel-header">
                      <span className="cancel-emoji">💪</span>
                      <h2>Laisse-nous une chance !</h2>
                      <p>On veut vraiment t'aider à atteindre tes objectifs</p>
                    </div>

                    <div className="offer-card">
                      <MessageCircle size={40} />
                      <h3>Session coaching offerte</h3>
                      <p>Un de nos experts nutrition va te contacter pour t'aider personnellement.</p>
                      <button className="accept-offer-btn" onClick={() => handleAcceptOffer('coaching')}>
                        Je veux ma session gratuite
                      </button>
                    </div>

                    <div className="offer-card special">
                      <div className="offer-badge">OFFRE SPÉCIALE</div>
                      <h3>1 mois offert</h3>
                      <p>On t'offre 1 mois supplémentaire pour te prouver notre valeur</p>
                      <button className="accept-offer-btn" onClick={() => handleAcceptOffer('free-month')}>
                        J'accepte le mois gratuit
                      </button>
                    </div>

                    <button className="skip-offer" onClick={() => setCancelStep(3)}>
                      Non merci
                    </button>
                  </>
                )}

                {(cancelReason === 'found-alternative' || cancelReason === 'temporary' || cancelReason === 'other') && (
                  <>
                    <div className="cancel-header">
                      <span className="cancel-emoji">🙏</span>
                      <h2>Avant de partir...</h2>
                      <p>On a quelques offres qui pourraient te faire changer d'avis</p>
                    </div>

                    <div className="offers-stack">
                      <div className="offer-card mini">
                        <Gift size={24} />
                        <div>
                          <h4>2 mois offerts</h4>
                          <p>Continue gratuitement pendant 2 mois</p>
                        </div>
                        <button onClick={() => handleAcceptOffer('2months')}>
                          <ArrowRight size={18} />
                        </button>
                      </div>

                      <div className="offer-card mini">
                        <Crown size={24} />
                        <div>
                          <h4>-50% à vie</h4>
                          <p>4,49€/mois pour toujours</p>
                        </div>
                        <button onClick={() => handleAcceptOffer('50off')}>
                          <ArrowRight size={18} />
                        </button>
                      </div>

                      <div className="offer-card mini">
                        <Pause size={24} />
                        <div>
                          <h4>Pause 3 mois</h4>
                          <p>Sans perdre tes données</p>
                        </div>
                        <button onClick={() => handleAcceptOffer('pause3')}>
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    </div>

                    <button className="skip-offer" onClick={() => setCancelStep(3)}>
                      Aucune offre ne m'intéresse
                    </button>
                  </>
                )}
              </div>
            )}

            {cancelStep === 3 && (
              <div className="cancel-step final">
                <div className="cancel-header">
                  <span className="cancel-emoji">👋</span>
                  <h2>Dernière chance...</h2>
                  <p>Tu vas perdre tous ces avantages :</p>
                </div>

                <div className="losing-features">
                  <div className="losing-item">
                    <X size={18} />
                    <span>Messages illimités avec Louis</span>
                  </div>
                  <div className="losing-item">
                    <X size={18} />
                    <span>Votre historique de conversation</span>
                  </div>
                  <div className="losing-item">
                    <X size={18} />
                    <span>Recettes personnalisées</span>
                  </div>
                  <div className="losing-item">
                    <X size={18} />
                    <span>Support prioritaire</span>
                  </div>
                </div>

                <div className="final-offer">
                  <Gift size={24} />
                  <div>
                    <strong>Dernière offre : 3 mois à 1€</strong>
                    <p>Juste pour voir si on peut t'aider</p>
                  </div>
                  <button onClick={() => handleAcceptOffer('3months-1euro')}>
                    J'essaie
                  </button>
                </div>

                <div className="cancel-actions">
                  <button className="stay-btn" onClick={() => setShowCancelFlow(false)}>
                    Je reste Pro ! 💪
                  </button>
                  <button className="confirm-cancel-btn" onClick={handleFinalCancel}>
                    Confirmer l'annulation
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="modal-overlay" onClick={() => setShowSuccess(false)}>
          <div className="success-modal" onClick={e => e.stopPropagation()}>
            <div className="success-icon">🎉</div>
            <h2>Offre activée !</h2>
            <p>Merci de rester avec nous. Ton offre a été appliquée à ton compte.</p>
            <button onClick={() => setShowSuccess(false)}>Super !</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
